import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@clerk/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { MessageSquare, Send, Bot, User } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trackEvent, FUNNEL_EVENTS } from '@/lib/analytics';

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

interface Props {
  analysisId: number;
  marketId: string;
  platform: string;
  marketQuestion: string;
}

async function apiFetch(url: string, token: string | null, options?: RequestInit) {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export function AnalysisChat({ analysisId, marketId, platform, marketQuestion }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [input, setInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const chatKey = ['analysis-chat', analysisId];

  const { data, isLoading } = useQuery({
    queryKey: chatKey,
    enabled: isOpen,
    queryFn: async () => {
      const token = await getToken();
      const json = await apiFetch(`/api/analyses/${analysisId}/chat`, token);
      return json.messages as ChatMessage[];
    },
    staleTime: 30_000,
  });

  const messages = data ?? [];

  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      const token = await getToken();
      return apiFetch(`/api/analyses/${analysisId}/chat`, token, {
        method: 'POST',
        body: JSON.stringify({ content }),
      }) as Promise<ChatMessage>;
    },
    onMutate: async (content) => {
      // Optimistic user message
      const prev = qc.getQueryData<ChatMessage[]>(chatKey) ?? [];
      const optimistic: ChatMessage = {
        id: Date.now(),
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      };
      qc.setQueryData(chatKey, [...prev, optimistic]);
      return { prev };
    },
    onSuccess: (reply, content) => {
      qc.setQueryData<ChatMessage[]>(chatKey, (old) => {
        const withoutOptimistic = (old ?? []).filter((m) => m.id !== Date.now());
        return [...withoutOptimistic, reply];
      });
      // Refresh to get both the user message (with real id) + assistant reply
      qc.invalidateQueries({ queryKey: chatKey });
      trackEvent(FUNNEL_EVENTS.CHAT_MESSAGE_SENT, {
        marketId,
        platform,
        messageLength: content.length,
      });
    },
    onError: (_err, _content, ctx) => {
      if (ctx?.prev) qc.setQueryData(chatKey, ctx.prev);
    },
  });

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = () => {
    const content = input.trim();
    if (!content || sendMutation.isPending) return;
    setInput('');
    sendMutation.mutate(content);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSend();
    }
  };

  if (!isOpen) {
    return (
      <Button
        variant="outline"
        className="w-full"
        onClick={() => setIsOpen(true)}
      >
        <MessageSquare className="mr-2 h-4 w-4" />
        Ask Claude a follow-up question
      </Button>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          Follow-up Chat
          <span className="text-xs font-normal text-muted-foreground ml-1">
            Ask Claude anything about this analysis
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Message thread */}
        <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Spinner size="sm" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">
              <Bot className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
              Ask Claude anything about this market or its analysis
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  'flex gap-2',
                  msg.role === 'user' ? 'justify-end' : 'justify-start',
                )}
              >
                {msg.role === 'assistant' && (
                  <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div
                  className={cn(
                    'max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm',
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground',
                  )}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
                {msg.role === 'user' && (
                  <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                    <User className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
              </div>
            ))
          )}
          {sendMutation.isPending && (
            <div className="flex gap-2 justify-start">
              <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="bg-muted rounded-xl px-3.5 py-2.5">
                <Spinner size="sm" />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div className="flex gap-2 pt-1">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about the reasoning, edge size, risks…"
            className="min-h-[60px] resize-none text-sm"
            disabled={sendMutation.isPending}
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={!input.trim() || sendMutation.isPending}
            className="h-auto aspect-square"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          ⌘ + Enter to send · Claude has full context of this analysis
        </p>
      </CardContent>
    </Card>
  );
}
